import * as React from 'react';
import { TrendingUp, TrendingDown, AlertCircle, RotateCcw } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { cn } from '../../lib/utils';

export interface KpiCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  trend?: { value: number; direction: 'up' | 'down' };
  subtitle?: string;
  color?: 'primary' | 'success' | 'warning' | 'destructive' | 'muted';
  /** When true, renders an error state instead of the value */
  isError?: boolean;
  /** Callback to retry loading the KPI data */
  onRetry?: () => void;
  onClick?: () => void;
  className?: string;
}

const colorMap: Record<
  'primary' | 'success' | 'warning' | 'destructive' | 'muted',
  { bg: string; text: string; trend: string }
> = {
  primary: {
    bg: 'bg-[var(--color-primary)]/10',
    text: 'text-[var(--color-primary)]',
    trend: 'text-[var(--color-primary)]',
  },
  success: {
    bg: 'bg-[var(--color-success)]/10',
    text: 'text-[var(--color-success)]',
    trend: 'text-[var(--color-success)]',
  },
  warning: {
    bg: 'bg-[var(--color-warning)]/10',
    text: 'text-[var(--color-warning)]',
    trend: 'text-[var(--color-warning)]',
  },
  destructive: {
    bg: 'bg-[var(--color-destructive)]/10',
    text: 'text-[var(--color-destructive)]',
    trend: 'text-[var(--color-destructive)]',
  },
  muted: {
    bg: 'bg-[var(--color-muted)]/10',
    text: 'text-[var(--color-muted-foreground)]',
    trend: 'text-[var(--color-muted-foreground)]',
  },
};

export const KpiCard = React.forwardRef<HTMLDivElement, KpiCardProps>(
  (
    {
      title,
      value,
      icon,
      trend,
      subtitle,
      color = 'primary',
      isError = false,
      onRetry,
      onClick,
      className,
    },
    ref,
  ) => {
    const colorConfig = colorMap[color];
    const isClickable = !!onClick || (isError && !!onRetry);

    return (
      <Card
        ref={ref}
        onClick={isError && onRetry ? onRetry : onClick}
        className={cn(
          'flex items-center justify-between p-6',
          isClickable && 'cursor-pointer transition-shadow hover:shadow-md',
          isError && 'border-[var(--color-destructive)]/30',
          className,
        )}
      >
        <CardContent className="flex items-center gap-4 p-0">
          {icon && (
            <div className={cn('rounded-lg p-3', isError ? 'bg-[var(--color-destructive)]/10' : colorConfig.bg)}>
              <div className={cn('flex items-center justify-center', isError ? 'text-[var(--color-destructive)]' : colorConfig.text)}>
                {isError ? <AlertCircle className="h-5 w-5" /> : icon}
              </div>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-[var(--color-muted-foreground)]">
              {title}
            </p>
            {isError ? (
              <p className="text-2xl font-bold text-[var(--color-destructive)]">
                &mdash;
              </p>
            ) : (
              <p className="text-2xl font-bold text-[var(--color-foreground)]">
                {value}
              </p>
            )}
            {isError ? (
              <p className="text-xs text-[var(--color-destructive)]/80 flex items-center gap-1">
                {onRetry && <RotateCcw className="h-3 w-3" />}
                {onRetry ? 'Click to retry' : 'Failed to load'}
              </p>
            ) : subtitle ? (
              <p className="text-xs text-[var(--color-muted-foreground)]">
                {subtitle}
              </p>
            ) : null}
          </div>
        </CardContent>

        {!isError && trend && (
          <div className="flex items-center gap-1">
            <div
              className={cn(
                'flex items-center gap-0.5 rounded px-2 py-1',
                trend.direction === 'up'
                  ? 'bg-[var(--color-success)]/10'
                  : 'bg-[var(--color-destructive)]/10',
              )}
            >
              {trend.direction === 'up' ? (
                <TrendingUp className="h-4 w-4 text-[var(--color-success)]" />
              ) : (
                <TrendingDown className="h-4 w-4 text-[var(--color-destructive)]" />
              )}
              <span
                className={cn(
                  'text-xs font-semibold',
                  trend.direction === 'up'
                    ? 'text-[var(--color-success)]'
                    : 'text-[var(--color-destructive)]',
                )}
              >
                {Math.abs(trend.value)}%
              </span>
            </div>
          </div>
        )}
      </Card>
    );
  },
);

KpiCard.displayName = 'KpiCard';
